/**
 * Formats a user's first and last name into a display name
 * Falls back to a default if no name is provided
 */
export const formatUserDisplayName = (
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  fallback: string = 'Anonymous User'
): string => {
  const fullName = `${firstName || ''} ${lastName || ''}`.trim();
  return fullName || fallback;
};

/**
 * Formats a date to a readable string
 * Handles both Date objects and ISO date strings (from cache)
 */
export const formatDate = (date: Date | string): string => {
  const dateObj = date instanceof Date ? date : new Date(date);
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(dateObj);
};

/**
 * Formats a match date for display in game rows
 * Handles both Date objects and ISO date strings (from cache)
 */
export const formatMatchDate = (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
  const dateObj = date instanceof Date ? date : new Date(date);
  const defaultOptions: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  };
  return dateObj.toLocaleDateString('en-GB', options || defaultOptions);
};

/**
 * Formats a score (e.g., "2 - 1")
 */
export const formatScore = (homeScore: number, awayScore: number): string => {
  return `${homeScore} - ${awayScore}`;
};

/**
 * Capitalizes the first letter of a string
 */
export const capitalize = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Updates the theme-color meta tag for mobile browsers
 * This colors the status bar area on mobile devices
 */
export const updateThemeColor = (color: string): void => {
  let themeColorMeta = document.querySelector('meta[name="theme-color"]');

  if (!themeColorMeta) {
    themeColorMeta = document.createElement('meta');
    themeColorMeta.setAttribute('name', 'theme-color');
    document.head.appendChild(themeColorMeta);
  }

  themeColorMeta.setAttribute('content', color);
};
